import css from './css/DashboardMsisdnPage.module.css'
import {Alert, Col, Row, Table} from "react-bootstrap";
import MsisdnOverviewRow from '../components/MsisdnOverviewRow';
import React, {useEffect, useRef, useState} from 'react';
import {getMsisdnOverview, sendFcmMessage} from '../API/msisdnApi';
import {toast} from 'react-toastify';
import messageFromError from '../utils/errorUtils';
import LoadingTableSpinner from '../components/Loading/LoadingTableSpinner';
import useCompanyId from '../Hooks/useCompanyId';
import {useAuth0} from '@auth0/auth0-react';
import IconButton from "../components/IconButton";
import {faCircleInfo, faRefresh} from "@fortawesome/free-solid-svg-icons";
import ToggleButtons from "../components/ToggleButtons";
import DashboardMap from "../components/DashboardMap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ConditionalRender from "../components/ConditionalRender";
import getGlobalConfig from "../globalConfig";
import * as signalR from '@microsoft/signalr';
import PaginationComponent from "../components/core/Pagination/PaginationComponent";
import {useTranslation} from "react-i18next";
import Search from "../components/core/Components/Search";
import SendMessageToMsisdnModal from "../components/Modals/SendMessageToMsisdnModal";
import useMyTranslation from "../Hooks/useMyTranslation";
import {differenceInSecondsDates, formatISODate} from "../utils/DateTimeUtills";
import RealTimeDisplay from "../components/RealTimeDisplay";
import {differenceInTimeToText} from "../utils/utils";

export default function DashboardMsisdnsPage()
{
    const companyId = useCompanyId()
    const { getAccessTokenSilently } = useAuth0();
    const {t} = useTranslation()
    const myT = useMyTranslation('pages.dashboard')

    const connectionInfo = useRef({connection: null, isConnecting: false})

    const [msisdnToSendMessage, setMsisdnToSendMessage] = useState(null)

    const [overviews, setOverviews] = useState({data: [], size: 0, totalElements: 0, totalPages: 0, lastPage: 0})
    const [search, setSearch] = useState('')
    const [showLoading, setShowLoading] = useState(false)
    const [view, setView] = useState('list')
    const [lastUpdate, setLastUpdate] = useState(new Date())
    const pageRef = useRef(0)
    const firstFetch = useRef(true)

    useEffect(() => {
        fetchOverview(true)
    }, [])

    useEffect(() => {
        fetchOverview(true)
    }, [search]);

      useEffect(() => {
        if(companyId)
        {
            if(!connectionInfo.current.isConnecting && !connectionInfo.current.connection)
            {
                establishConnection();
            }
        }

        return () => {
          if(connectionInfo.current.connection)
          {
              connectionInfo.current.connection.stop();
          }
        }

      }, [companyId]);



    async function establishConnection()
    {
        try {
            connectionInfo.current.isConnecting = true;

            // Create a SignalR connection
            const newConnection = new signalR.HubConnectionBuilder()
                .withUrl(getGlobalConfig().baseUrl + 'v1', {withCredentials: true, accessTokenFactory: async () => { return await getAccessTokenSilently() }})
                .withAutomaticReconnect()
                .build();

            // Set up event listeners
            newConnection.on('dashboardUpdate', async () => {
                try {
                   await fetchOverview(false)
                } catch (error) {
                    toast.error(messageFromError(error));
                }
            });

            await newConnection.start();
            connectionInfo.current.connection = newConnection;
        }
        catch(error)
        {
            toast.error("Error establishing socket connection. The information won't be updated automatically.")
            console.error('Error establishing connection:', error)
        }
        finally {
            connectionInfo.current.isConnecting = false;
        }
    }

    const fetchOverview = async (showLoading = false) => {
        try {
            if (showLoading || firstFetch.current) {
                setShowLoading(true)
            }
            const response = await getMsisdnOverview(companyId, pageRef.current, search)
            setOverviews(response.data)
            setLastUpdate(new Date())
            firstFetch.current = false
        } catch (error) {
            console.log(error);
            toast.error(messageFromError(error));
        } finally {
            setShowLoading(false)
        }
    }

    async function requestStatusUpdate()
    {
        toast.success('Refresh Request sent')
        for (const overview of overviews.data) {
            try {
                await sendFcmMessage(overview.msisdn.id, {
                    type: 'SEND_PING'
                })
            }
            catch(error)
            {
                //Ignore
            }
        }
    }

    // if(overviews.size === 0)
    //     return <LoadingSpinner/>

    return(
        <div className={`${css.wrapper} content-full`}>
            <Row className='justify-content-center'>
                <Col lg={12}>
                <div className={'px-0 container-full'}>
                        <Row className='align-items-center mb-2'>
                            <Col lg={4} md={8} sm={7} className={'d-flex flex-column align-items-start'}>
                                <h1>{t("pages.dashboard.title")}</h1>
                                <Search onSearch={(t) => setSearch(t)} />
                            </Col>
                            <Col className='center align-self-end'>
                                <ToggleButtons
                                    onChange={(value) => setView(value)}
                                    buttons={[
                                        {
                                            value: 'list',
                                            text: myT('list'),
                                        },
                                        {
                                            value: 'map',
                                            text: myT('map'),
                                        }
                                    ]} />
                            </Col>
                            <Col className='end align-self-end d-flex align-items-center'>
                                <small className={'small-text me-2'}>
                                    <span>{myT('lastRefreshed')}: </span>
                                    <span>
                                        <RealTimeDisplay updateInterval={1000}>
                                            {(currentTime) =>
                                                differenceInSecondsDates(lastUpdate, currentTime) < 3600
                                                    ? differenceInTimeToText(lastUpdate, currentTime, t)
                                                    : formatISODate(lastUpdate.toISOString(), { format: "timeOnly", useLocalTime: true })
                                            }
                                        </RealTimeDisplay>
                                    </span>
                                </small>
                                <IconButton icon={faRefresh} bg='#eeeee' onClick={() => fetchOverview(true)}/>
                            </Col>
                        </Row>
                    </div>

                    {view === 'list' ?
                        <>
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th width={30}/>
                                        <th className={'text-start'}>{myT('employee')}</th>
                                        <th width={150} className={'text-start'}>{myT('phone')}</th>
                                        <th width={100}>{myT('startWorkday')}</th>
                                        <th width={100}>{myT('endWorkday')}</th>
                                        <th>{myT('currentWorkLocation')}</th>
                                        <th>{myT('previousWorkLocations')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                {showLoading && <LoadingTableSpinner small variant={'primary'}
                                                                     text={t('pages.dashboard.generatingOverview') + '...'}
                                                                     columns={7}/>}

                                {!showLoading && overviews.data.length > 0 && overviews.data.map(overview => {
                                    return <MsisdnOverviewRow key={overview.msisdn.id} overview={overview} onMsisdnToSendMessageSelected={(msisdn) => setMsisdnToSendMessage(msisdn)}/>
                                })}
                                </tbody>
                            </Table>

                            {!showLoading && overviews.data.length < 1 &&
                                <div className='d-flex justify-content-center mt-5'>
                                    <Alert variant='warning' className='w-100'>
                                        <FontAwesomeIcon icon={faCircleInfo}/>
                                        <span className='ms-2'>{t('pages.dashboard.noWorkers')}</span>
                                    </Alert>
                                </div>
                            }
                        </>

                        :

                        <DashboardMap overviews={overviews.data}/>
                    }
                </Col>
            </Row>
            <ConditionalRender condition={overviews.totalPages > 1}>
                <Row className={'mt-3'}>
                    <Col className={'d-flex justify-content-center'}>
                        <PaginationComponent lastPage={overviews.lastPage} onPageChanged={(page) => { pageRef.current = page; fetchOverview(true);}}/>
                    </Col>
                </Row>
            </ConditionalRender>


            <SendMessageToMsisdnModal
                show={msisdnToSendMessage}
                onHide={() => {setMsisdnToSendMessage(null)}}
                msisdn={msisdnToSendMessage}
            />

        </div>
    )
}